import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Enums } from "@bednbody/types/database";
import { supabase } from "@/lib/supabase";
import { hasBookingHistory } from "./staff-detail.access";

/** Enough history for a staff member at pilot scale; the table pages it. */
const HISTORY_LIMIT = 500;

/**
 * BL-41 — one therapist's or rider's bookings, newest first.
 *
 * The same columns as `useOpsBookings` and nothing more: no client identity is
 * selected, and RLS hub-scopes a hub manager exactly as it does on the Bookings
 * page. `amount_due` comes back for every role because the Bookings page reads
 * it too — the modal decides whether to SHOW it (`canSeeMoney`).
 *
 * Keyed on the row's own staff column: a group booking is one row per client,
 * each carrying its therapist, so a therapist's rows are exactly her work. A
 * rider carries every client of a group, so the caller groups by booking.
 */
export function useStaffBookings(staff: { id: string; role: Enums<"user_role"> } | null) {
  const enabled = staff !== null && hasBookingHistory(staff.role);
  return useQuery({
    queryKey: ["ops-bookings", "staff", staff?.id ?? null],
    enabled,
    queryFn: async () => {
      if (!staff) return [];
      const column = staff.role === "rider" ? "rider_id" : "therapist_id";
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `id, ref, status, scheduled_at, hub_id, therapist_id, rider_id,
           payment_method, gross_amount, amount_due, booking_group_id,
           hubs ( name, region, is_pilot ),
           service_variants ( duration_minutes, services ( name ) )`,
        )
        .eq(column, staff.id)
        .order("scheduled_at", { ascending: false })
        .limit(HISTORY_LIMIT);
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });
}

export interface StaffEarnings {
  /** Σ every payroll line, whatever the run's status. */
  total: number;
  /** Σ the lines whose run is `paid`. */
  paid: number;
  latest: { periodStart: string; periodEnd: string; total: number; status: string } | null;
}

/**
 * BL-40 — what payroll has posted for this person.
 *
 * ⚠ `payroll_lines`, NOT `therapist_daily_earnings`. Both carry money, but only
 * payroll_lines is walled off from `hub_manager` at the grant level (0010:33),
 * so even if this query were reached by a hub manager it would return nothing.
 * `therapist_daily_earnings` IS readable by a hub manager for their hub (it
 * drives the dispatch queue), and reading it here would make the hub manager's
 * exclusion a UI promise instead of a database one.
 *
 * It is POSTED pay: tonight's unsettled Session is not in it until the nightly
 * run writes the line. The modal labels it that way.
 */
export function useStaffEarnings(staffId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["staff-earnings", staffId],
    enabled: enabled && staffId !== null,
    queryFn: async (): Promise<StaffEarnings> => {
      const { data, error } = await supabase
        .from("payroll_lines")
        .select("total, payroll_runs ( period_start, period_end, status )")
        .eq("staff_id", staffId!);
      if (error) throw error;

      let total = 0;
      let paid = 0;
      let latest: StaffEarnings["latest"] = null;
      for (const line of data ?? []) {
        const amount = Number(line.total);
        total += amount;
        const run = line.payroll_runs;
        if (!run) continue;
        if (run.status === "paid") paid += amount;
        if (!latest || run.period_end > latest.periodEnd) {
          latest = {
            periodStart: run.period_start,
            periodEnd: run.period_end,
            total: amount,
            status: run.status ?? "draft",
          };
        }
      }
      // Summed as numbers, rounded once — every term is already numeric(10,2).
      return { total: round2(total), paid: round2(paid), latest };
    },
    staleTime: 60_000,
  });
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The per-staff commission rate (money-math.md §2 — never a literal). Read on
 * its own, and only when the viewer may see money: putting it on the roster
 * query would ship every rate to every hub manager who opens the Roster.
 */
export function useStaffCommissionRate(staffId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["staff-commission-rate", staffId],
    enabled: enabled && staffId !== null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff")
        .select("commission_rate")
        .eq("id", staffId!)
        .single();
      if (error) throw error;
      return data.commission_rate;
    },
    staleTime: 5 * 60_000,
  });
}

export interface StaffPayoutRow {
  id: string;
  createdAt: string;
  amount: number;
  status: Enums<"payout_status">;
  settledAt: string | null;
  providerRef: string | null;
  failureReason: string | null;
}

/**
 * BL-24 — every withdrawal this staff member has asked for (TEND-059).
 *
 * ⚠ THIS IS NOT THE 7-COLUMN DAILY LEDGER the intake specifies (date, daily
 * balance, withdrawal status, cash, online, total, commission on cash only).
 * Three of those columns have no definition anywhere — "daily balance",
 * "total" and "total commission (cash only)" — and inventing pay arithmetic is
 * exactly what `never-invent-policy.md` forbids. The ledger stays an Open Item
 * for Luigi; this is the half that is already real data.
 *
 * `payout_requests` is granted to admin and superadmin only (0156:85, 0218 for
 * riders) — no hub_manager, no executive — so the database refuses this read
 * for anyone else and the caller passes `enabled` to match.
 */
export function useStaffPayouts(staffId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["staff-payouts", staffId],
    enabled: enabled && staffId !== null,
    queryFn: async (): Promise<StaffPayoutRow[]> => {
      const { data, error } = await supabase
        .from("payout_requests")
        .select("id, created_at, amount_php, status, settled_at, provider_ref, failure_reason")
        .eq("staff_id", staffId!)
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        createdAt: row.created_at,
        amount: Number(row.amount_php),
        status: row.status,
        settledAt: row.settled_at,
        providerRef: row.provider_ref,
        failureReason: row.failure_reason,
      }));
    },
    staleTime: 60_000,
  });
}

// ── 0311: the six service switches ─────────────────────────────────────────

export type ServiceSwitch =
  | "full_body"
  | "deep_tissue"
  | "pregnancy"
  | "kiddie"
  | "ventosa"
  | "hot_stone";

/** 0311's derived group: Premium Therapeutic, held when Ventosa and Hotstone are. Read-only. */
export type ReadableSwitch = ServiceSwitch | "premium_both";

// 0311's functions are not in the generated types until it is pushed.
const untypedRpc = supabase as unknown as {
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): PromiseLike<{ data: unknown; error: { message: string } | null }>;
};

export function useServiceSwitches(therapistId: string | null) {
  return useQuery({
    queryKey: ["service-switches", therapistId],
    enabled: therapistId !== null,
    queryFn: async () => {
      const { data, error } = await untypedRpc.rpc("therapist_service_switches", {
        p_staff_id: therapistId,
      });
      if (error) throw new Error(error.message);
      return (data ?? {}) as Partial<Record<ReadableSwitch, boolean>>;
    },
  });
}

export function useSetServiceSwitch(therapistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, on }: { key: ServiceSwitch; on: boolean }) => {
      const { error } = await untypedRpc.rpc("set_therapist_service_switch", {
        p_staff_id: therapistId,
        p_switch: key,
        p_on: on,
      });
      if (error) throw new Error(error.message);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["service-switches", therapistId] }),
  });
}
